import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { MapPin, Gift, Clock, AlertTriangle } from 'lucide-react';
import axios from 'axios';

import { getBackendUrl } from '../services/origin';
import CookieConsentBanner from '../components/CookieConsentBanner';
import CookieFooterLink from '../components/CookieFooterLink';
import { loadTracking } from '../services/tracking';
const API = getBackendUrl();
const BASE = getBackendUrl() || '';

export default function CampaignLanding() {
  const { campaignId } = useParams();
  const [searchParams] = useSearchParams();
  
  // Preview mode: ?step=X&preview=true
  const isPreview = searchParams.get('preview') === 'true';
  const previewStep = searchParams.get('step') || 'landing';
  // Auto-recognition: ?dt=<dashboard_token> appended by the dashboard's
  // QR scanner when the customer is already authenticated. Lets us skip
  // the phone+PIN form entirely. Fallback: if the URL has no `?dt=` (the
  // customer scanned a printed flyer's QR directly with the OS camera),
  // try to read the token from localStorage where the dashboard stores
  // it on PIN verification.
  const dashboardToken = (
    searchParams.get('dt')
    || (typeof window !== 'undefined' && window.localStorage
          ? (localStorage.getItem('lazo_dashboard_token') || '')
          : '')
  );
  
  const [campaign, setCampaign] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [step, setStep] = useState(isPreview ? previewStep : 'landing'); // landing | login | scratch | thankyou
  const [phone, setPhone] = useState('');
  const [pin, setPin] = useState('');
  const [revealing, setRevealing] = useState(false);
  const [prize, setPrize] = useState(isPreview && (previewStep === 'scratch' || previewStep === 'thankyou') ? {
    prize_name: 'Premio Esempio',
    coupon_type: 'prize',
    status: 'available',
    customer_name: 'Mario Rossi',
    dashboard_token: 'preview_token',
    expires_at: new Date(Date.now() + 30 * 86400000).toISOString(),
  } : null);
  const [revealError, setRevealError] = useState('');
  // Identity from token (auto-recognized customer)
  const [tokenCustomer, setTokenCustomer] = useState(null);

  useEffect(() => {
    axios.get(`${API}/api/public/campaigns/${campaignId}`, { validateStatus: () => true })
      .then(r => {
        if (r.status < 200 || r.status >= 300) throw new Error('not found');
        setCampaign(r.data);
        if (r.data?.brand_id) loadTracking(r.data.brand_id);
      })
      .catch(() => setError('Campagna non trovata'))
      .finally(() => setLoading(false));
  }, [campaignId]);

  // When arrived from inside the customer's authenticated dashboard
  // (URL contains `?dt=<token>`), look up the customer once we have the
  // campaign loaded so we can render a personalized landing.
  useEffect(() => {
    if (!dashboardToken || !campaign || tokenCustomer) return;
    axios.get(
      `${API}/api/public/campaigns/${campaignId}/customer-by-token`,
      { params: { token: dashboardToken }, validateStatus: () => true }
    ).then(r => {
      if (r.status >= 200 && r.status < 300) setTokenCustomer(r.data);
      // On error we silently fall back to the anonymous landing — the
      // customer can still log in manually with phone+PIN.
    }).catch(() => {});
  }, [dashboardToken, campaign, campaignId, tokenCustomer]);

  const handleRevealByToken = async () => {
    if (!dashboardToken) return;
    setRevealing(true);
    setRevealError('');
    try {
      const res = await axios.post(
        `${API}/api/public/campaigns/${campaignId}/reveal-by-token`,
        { dashboard_token: dashboardToken },
        { headers: { 'Content-Type': 'application/json' }, validateStatus: () => true }
      );
      if (res.status < 200 || res.status >= 300) {
        const detail = (res.data && (res.data.detail || res.data.message)) || `Errore ${res.status}`;
        throw new Error(detail);
      }
      setPrize(res.data);
      setStep('scratch');
    } catch (err) {
      setRevealError(err.message || 'Errore sconosciuto');
    } finally {
      setRevealing(false);
    }
  };

  const c = campaign || {};
  const landingAccent = c.landing_accent_color || c.primary_color || '#D44E20';
  const landingBg = c.landing_bg_color || '#111114';
  const loginAccent = c.login_accent_color || c.primary_color || '#D44E20';
  const loginBg = c.login_bg_color || '#111114';
  const scratchAccent = c.scratch_accent_color || c.primary_color || '#D44E20';
  const scratchBg = c.scratch_bg_color || '#111114';
  const tyAccent = c.thankyou_accent_color || c.primary_color || '#D44E20';
  const tyBg = c.thankyou_bg_color || '#111114';

  const handleReveal = async () => {
    if (!phone.trim() || !pin.trim()) return setRevealError('Inserisci telefono e PIN');
    setRevealing(true);
    setRevealError('');
    try {
      // Use axios for robust body parsing — raw fetch.text()/json() can
      // race against the service-worker response pipeline on some Chrome
      // builds and throw "body stream already read".
      const res = await axios.post(
        `${API}/api/public/campaigns/${campaignId}/reveal`,
        { phone, pin },
        { headers: { 'Content-Type': 'application/json' }, validateStatus: () => true }
      );
      if (res.status < 200 || res.status >= 300) {
        const detail = (res.data && (res.data.detail || res.data.message)) || `Errore ${res.status}`;
        throw new Error(detail);
      }
      setPrize(res.data);
      setStep('scratch');
    } catch (err) {
      setRevealError(err.message || 'Errore sconosciuto');
    } finally {
      setRevealing(false);
    }
  };

  if (loading) return (
    <div className="min-h-dvh flex items-center justify-center" style={{ backgroundColor: '#111114' }}>
      <div className="w-8 h-8 border-2 border-[#D44E20] border-t-transparent rounded-full animate-spin" />
    </div>
  );

  if (error) return (
    <div className="min-h-dvh flex items-center justify-center p-6" style={{ backgroundColor: '#111114' }}>
      <div className="text-center">
        <AlertTriangle size={48} className="mx-auto mb-4 text-[#FFAA00]" />
        <p className="text-white text-lg font-bold">{error}</p>
      </div>
    </div>
  );

  const PreviewBanner = () => isPreview ? (
    <div className="fixed top-0 left-0 right-0 z-[100] bg-[#FFAA00] text-[#111] text-center py-2 text-sm font-bold tracking-wide" data-testid="preview-banner">
      ANTEPRIMA — {step === 'landing' ? 'Landing' : step === 'login' ? 'Login' : step === 'scratch' ? 'Scratch Card' : 'Thank You'}
    </div>
  ) : null;

  // ═══════════════════════════════════════════════════════════════════════
  // PAGE 4: THANK YOU
  // ═══════════════════════════════════════════════════════════════════════
  if (step === 'thankyou' && prize) {
    const isAvailable = prize.status === 'available';
    const title = c.thankyou_title || 'Complimenti!';
    const subtitle = c.thankyou_subtitle || 'Mostra questa schermata in cassa per riscattare il tuo premio';
    const ctaText = c.thankyou_cta_text || 'Vai alla tua bacheca';
    const dashboardUrl = prize.dashboard_token ? `${BASE}/my/${prize.dashboard_token}` : '';

    return (
      <div className="min-h-dvh flex flex-col items-center justify-center p-6" style={{ background: `linear-gradient(135deg, ${tyAccent}22 0%, ${tyBg} 50%, ${tyAccent}11 100%)` }} data-testid="page-thankyou">
        <PreviewBanner />
        {isAvailable && <Confetti accent={tyAccent} />}
        <div className="w-full max-w-sm text-center">
          {c.logo_url && <img src={c.logo_url} alt="" className="h-12 mx-auto mb-6 rounded-lg" />}

          {isAvailable ? (
            <>
              <div className="w-28 h-28 mx-auto mb-6 rounded-full flex items-center justify-center bg-[#3ECF8E]/20 border-2 border-[#3ECF8E]" style={{ animation: 'bounceIn 0.6s ease-out' }}>
                <Gift size={56} className="text-[#3ECF8E]" />
              </div>
              <p className="text-sm font-semibold mb-2 uppercase tracking-wider" style={{ color: tyAccent }}>{title} {prize.customer_name}!</p>
              <h1 className="text-white text-3xl font-bold mb-3">{prize.prize_name}</h1>
              <p className="text-[#888] text-sm mb-8">{subtitle}</p>
              <div className="border rounded-2xl p-5" style={{ backgroundColor: tyBg, borderColor: `${tyAccent}30` }}>
                <Gift size={32} style={{ color: tyAccent }} className="mx-auto mb-2" />
                <p className="text-white font-bold text-lg">{prize.prize_name}</p>
                <p className="text-[#888] text-xs mt-2">
                  {prize.expires_at && `Valido fino al ${new Date(prize.expires_at).toLocaleDateString('it-IT')}`}
                </p>
              </div>
              {dashboardUrl && (
                <a href={dashboardUrl} data-testid="go-to-dashboard-btn" className="block w-full h-14 mt-6 rounded-2xl font-bold text-lg text-white transition-all active:scale-95 text-center leading-[3.5rem]" style={{ backgroundColor: tyAccent }}>
                  {ctaText}
                </a>
              )}
            </>
          ) : prize.status === 'redeemed' ? (
            <>
              <div className="w-24 h-24 mx-auto mb-6 rounded-full flex items-center justify-center bg-gray-800 border-2 border-gray-600">
                <Gift size={48} className="text-gray-500" />
              </div>
              <p className="text-[#888] text-sm mb-2">Premio gia riscattato</p>
              <h2 className="text-white text-2xl font-bold mb-2">{prize.prize_name}</h2>
              <p className="text-[#888] text-sm">Riscattato il {prize.redeemed_at ? new Date(prize.redeemed_at).toLocaleDateString('it-IT') : ''}</p>
              {dashboardUrl && (
                <a href={dashboardUrl} className="block w-full h-14 mt-6 rounded-2xl font-bold text-lg text-white transition-all active:scale-95 text-center leading-[3.5rem]" style={{ backgroundColor: tyAccent }}>
                  {ctaText}
                </a>
              )}
            </>
          ) : (
            <>
              <div className="w-24 h-24 mx-auto mb-6 rounded-full flex items-center justify-center bg-gray-800 border-2 border-gray-600">
                <Clock size={48} className="text-[#FFAA00]" />
              </div>
              <p className="text-[#FFAA00] text-sm mb-2">Premio scaduto</p>
              <h2 className="text-white text-2xl font-bold">{prize.prize_name}</h2>
            </>
          )}
        </div>
        <style>{`@keyframes bounceIn { 0%{transform:scale(0)} 50%{transform:scale(1.2)} 100%{transform:scale(1)} }`}</style>
      </div>
    );
  }

  // ═══════════════════════════════════════════════════════════════════════
  // PAGE 3: SCRATCH CARD
  // ═══════════════════════════════════════════════════════════════════════
  if (step === 'scratch' && prize) {
    const scratchTitle = c.scratch_title || 'Gratta la card per scoprire il tuo premio!';

    return (
      <div className="min-h-dvh flex flex-col items-center justify-center p-6" style={{ backgroundColor: scratchBg }} data-testid="page-scratch">
        <PreviewBanner />
        <div className="w-full max-w-sm text-center">
          {c.logo_url && <img src={c.logo_url} alt="" className="h-12 mx-auto mb-6 rounded-lg" />}
          <p className="text-[#888] text-sm mb-6">{scratchTitle}</p>
          <ScratchCard
            prizeName={prize.prize_name}
            accent={scratchAccent}
            onComplete={() => setStep('thankyou')}
          />
        </div>
      </div>
    );
  }

  // ═══════════════════════════════════════════════════════════════════════
  // PAGE 2: LOGIN (phone + PIN)
  // ═══════════════════════════════════════════════════════════════════════
  if (step === 'login') {
    const loginTitle = c.login_title || 'Scopri il tuo premio!';
    const loginSub = c.login_subtitle || 'Inserisci il tuo numero e PIN per rivelare il premio';

    return (
      <div className="min-h-dvh flex flex-col items-center justify-center p-6" style={{ backgroundColor: loginBg }} data-testid="page-login">
        <PreviewBanner />
        <div className="w-full max-w-sm">
          {c.logo_url && <img src={c.logo_url} alt="" className="h-12 mx-auto mb-6 rounded-lg" />}
          <h2 className="text-white text-2xl font-bold text-center mb-2">{loginTitle}</h2>
          <p className="text-[#888] text-sm text-center mb-8">{loginSub}</p>

          <div className="space-y-4">
            <div>
              <label className="text-[#888] text-sm block mb-1">Numero di telefono</label>
              <input type="tel" value={phone} onChange={e => setPhone(e.target.value)} placeholder="3331234567" data-testid="reveal-phone" className="w-full h-14 text-white rounded-2xl px-5 text-lg border border-white/10 focus:outline-none" style={{ backgroundColor: loginBg === '#111114' ? '#18181E' : `${loginBg}cc`, borderColor: revealing ? loginAccent : undefined }} />
            </div>
            <div>
              <label className="text-[#888] text-sm block mb-1">PIN</label>
              <input type="number" value={pin} onChange={e => setPin(e.target.value)} placeholder="1234" maxLength={4} data-testid="reveal-pin" className="w-full h-14 text-white rounded-2xl px-5 text-lg border border-white/10 focus:outline-none text-center tracking-[0.5em]" style={{ backgroundColor: loginBg === '#111114' ? '#18181E' : `${loginBg}cc` }} />
            </div>
          </div>

          {revealError && <p className="text-red-400 text-sm text-center mt-3">{revealError}</p>}

          <button onClick={handleReveal} disabled={revealing} data-testid="reveal-btn" className="w-full h-14 mt-6 rounded-2xl font-bold text-lg text-white transition-all active:scale-95 disabled:opacity-50" style={{ backgroundColor: loginAccent }}>
            {revealing ? 'Caricamento...' : 'Scopri il premio!'}
          </button>

          <button onClick={() => setStep('landing')} className="w-full text-[#888] text-sm mt-4 py-2 hover:text-white transition-colors">Torna indietro</button>
        </div>
      </div>
    );
  }

  // ═══════════════════════════════════════════════════════════════════════
  // PAGE 1: LANDING (anonymous scan)
  // ═══════════════════════════════════════════════════════════════════════
  const ctaText = c.landing_cta_text || 'Sei gia registrato? Scopri il premio!';
  const footerText = c.landing_footer_text || 'Se non sei ancora registrato, presentati al locale e ti registreremo!';

  return (
    <div className="min-h-dvh flex flex-col" style={{ backgroundColor: landingBg }} data-testid="page-landing">
      <PreviewBanner />
      <CookieConsentBanner brandId={c.brand_id} />
      {c.landing_hero_url && (
        <div className="relative w-full" style={{ maxHeight: '280px', overflow: 'hidden' }}>
          <img src={c.landing_hero_url} alt="" className="w-full h-full object-cover" style={{ maxHeight: '280px' }} />
          <div className="absolute inset-0" style={{ background: `linear-gradient(to bottom, transparent 40%, ${landingBg} 100%)` }} />
        </div>
      )}

      <div className="flex-1 flex flex-col items-center justify-center p-6" style={c.landing_hero_url ? { marginTop: '-40px', position: 'relative', zIndex: 1 } : {}}>
        <div className="w-full max-w-sm text-center">
          {c.logo_url && !c.landing_hero_url && <img src={c.logo_url} alt="" className="h-16 mx-auto mb-8 rounded-xl" />}

          {!c.landing_hero_url && (
            <div className="w-24 h-24 mx-auto mb-6 rounded-full flex items-center justify-center" style={{ backgroundColor: `${landingAccent}20`, border: `2px solid ${landingAccent}` }}>
              <Gift size={48} style={{ color: landingAccent }} />
            </div>
          )}

          <h1 className="text-white text-2xl font-bold mb-3 leading-tight">{c.landing_title}</h1>
          {c.landing_subtitle && <p className="text-[#888] text-base mb-6">{c.landing_subtitle}</p>}
          <p className="text-white font-semibold text-lg mb-8">{c.business_name}</p>

          {/* Personalized greeting when customer arrived from their
              authenticated dashboard. Shown above the CTA so the user
              instantly understands they're recognized — no need to type
              phone/PIN again. */}
          {tokenCustomer && tokenCustomer.customer_name && (
            <div data-testid="campaign-greeting" className="rounded-2xl p-4 mb-5 border" style={{ backgroundColor: `${landingAccent}18`, borderColor: `${landingAccent}55` }}>
              <p className="text-white text-sm">Ciao <strong className="font-bold">{tokenCustomer.customer_name}</strong> 👋</p>
              <p className="text-[#cfcfcf] text-xs mt-1">
                {tokenCustomer.has_prize
                  ? 'Hai un premio pronto da scoprire!'
                  : 'Sei riconosciuto, ma per questa campagna non ti e ancora stato assegnato un premio. Vieni in cassa con il QR del volantino.'}
              </p>
            </div>
          )}

          {c.address && (
            <div className="rounded-2xl p-4 border border-white/5 mb-6" style={{ backgroundColor: landingBg === '#111114' ? '#18181E' : `${landingBg}dd` }}>
              <div className="flex items-center gap-3">
                <MapPin size={20} style={{ color: landingAccent }} className="flex-shrink-0" />
                <div className="text-left">
                  <p className="text-white text-sm font-medium">{c.address}</p>
                  {c.maps_url && (
                    <a href={c.maps_url} target="_blank" rel="noopener noreferrer" className="text-xs hover:underline" style={{ color: landingAccent }}>Apri in Google Maps</a>
                  )}
                </div>
              </div>
            </div>
          )}

          {tokenCustomer && tokenCustomer.has_prize ? (
            <button
              onClick={handleRevealByToken}
              disabled={revealing}
              data-testid="reveal-by-token-btn"
              className="w-full h-14 rounded-2xl font-bold text-lg text-white transition-all active:scale-95 shadow-lg disabled:opacity-60"
              style={{ backgroundColor: landingAccent }}
            >
              {revealing ? 'Caricamento…' : 'Scopri il tuo premio!'}
            </button>
          ) : tokenCustomer ? (
            // Recognized customer but no prize for THIS campaign yet.
            // Don't push them back through the login form — the backend
            // would just say "vieni al locale", which is exactly what the
            // greeting above already explains. Skip the CTA entirely.
            null
          ) : (
            <button onClick={() => setStep('login')} data-testid="already-registered-btn" className="w-full h-14 rounded-2xl font-bold text-lg text-white transition-all active:scale-95 shadow-lg" style={{ backgroundColor: landingAccent }}>
              {ctaText}
            </button>
          )}
          {revealError && <p className="text-red-400 text-sm mt-3" data-testid="reveal-by-token-error">{revealError}</p>}
          {!tokenCustomer && <p className="text-[#555] text-xs mt-4">{footerText}</p>}

          {!c.is_active && (
            <div className="mt-6 bg-[#FFAA00]/10 border border-[#FFAA00]/30 rounded-xl p-3 flex items-center gap-2">
              <Clock size={16} className="text-[#FFAA00]" />
              <span className="text-[#FFAA00] text-sm">Questa campagna non e piu attiva</span>
            </div>
          )}

          {/* Discreet cookie management trigger (GDPR art. 7.3) */}
          <div className="mt-8 flex justify-center text-white/40">
            <CookieFooterLink />
          </div>
        </div>
      </div>
    </div>
  );
}


// ─── SCRATCH CARD ───────────────────────────────────────────────────────────
function ScratchCard({ prizeName, accent, onComplete }) {
  const canvasRef = useRef(null);
  const isDrawing = useRef(false);
  const [pct, setPct] = useState(0);
  const completed = useRef(false);

  const init = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const dpr = window.devicePixelRatio || 1;
    const w = canvas.parentElement.offsetWidth;
    const h = 200;
    canvas.width = w * dpr;
    canvas.height = h * dpr;
    canvas.style.width = w + 'px';
    canvas.style.height = h + 'px';
    ctx.scale(dpr, dpr);
    ctx.fillStyle = accent || '#D44E20';
    ctx.fillRect(0, 0, w, h);
    ctx.globalAlpha = 0.15;
    ctx.fillStyle = '#000';
    for (let i = 0; i < w; i += 12) {
      for (let j = 0; j < h; j += 12) {
        if ((i + j) % 24 === 0) ctx.fillRect(i, j, 6, 6);
      }
    }
    ctx.globalAlpha = 1;
    ctx.fillStyle = '#fff';
    ctx.font = 'bold 20px Arial';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText('GRATTA QUI!', w / 2, h / 2 - 12);
    ctx.font = '14px Arial';
    ctx.globalAlpha = 0.7;
    ctx.fillText('Scorri per scoprire il premio', w / 2, h / 2 + 16);
    ctx.globalAlpha = 1;
  }, [accent]);

  useEffect(() => { init(); }, [init]);

  const scratch = (x, y) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    ctx.globalCompositeOperation = 'destination-out';
    ctx.beginPath();
    ctx.arc(x, y, 28, 0, Math.PI * 2);
    ctx.fill();
    ctx.globalCompositeOperation = 'source-over';
    const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
    let transparent = 0;
    for (let i = 3; i < imageData.data.length; i += 4) {
      if (imageData.data[i] === 0) transparent++;
    }
    const newPct = Math.round(transparent / (imageData.data.length / 4) * 100);
    setPct(newPct);
    if (newPct > 55 && !completed.current) {
      completed.current = true;
      setTimeout(onComplete, 300);
    }
  };

  const getPos = (e) => {
    const rect = canvasRef.current.getBoundingClientRect();
    const touch = e.touches?.[0];
    return { x: (touch ? touch.clientX : e.clientX) - rect.left, y: (touch ? touch.clientY : e.clientY) - rect.top };
  };

  const onStart = (e) => { e.preventDefault(); isDrawing.current = true; const p = getPos(e); scratch(p.x, p.y); };
  const onMove = (e) => { e.preventDefault(); if (!isDrawing.current) return; const p = getPos(e); scratch(p.x, p.y); };
  const onEnd = () => { isDrawing.current = false; };

  return (
    <div className="mb-6">
      <div className="relative rounded-2xl overflow-hidden border-2 border-white/10" style={{ minHeight: '200px' }}>
        <div className="absolute inset-0 flex flex-col items-center justify-center bg-[#18181E]">
          <Gift size={40} className="text-[#3ECF8E] mb-2" />
          <p className="text-[#3ECF8E] text-xl font-bold">{prizeName}</p>
        </div>
        <canvas ref={canvasRef} data-testid="scratch-card-canvas" className="relative z-10 touch-none cursor-crosshair"
          onMouseDown={onStart} onMouseMove={onMove} onMouseUp={onEnd} onMouseLeave={onEnd}
          onTouchStart={onStart} onTouchMove={onMove} onTouchEnd={onEnd}
        />
      </div>
      <div className="mt-2 h-1 bg-gray-800 rounded-full overflow-hidden">
        <div className="h-full rounded-full transition-all duration-300" style={{ width: `${Math.min(pct, 100)}%`, backgroundColor: accent }} />
      </div>
    </div>
  );
}

function Confetti({ accent }) {
  const colors = [accent || '#D44E20', '#3ECF8E', '#FFAA00', '#5B8DEF', '#FF6B9D'];
  const particles = Array.from({ length: 50 }, (_, i) => ({
    id: i, left: Math.random() * 100, delay: Math.random() * 0.6,
    duration: 1.2 + Math.random() * 1.5,
    color: colors[Math.floor(Math.random() * colors.length)],
    size: 4 + Math.random() * 10, rotation: Math.random() * 360,
  }));
  return (
    <div className="fixed inset-0 pointer-events-none z-50 overflow-hidden">
      {particles.map(p => (
        <div key={p.id} className="absolute" style={{
          left: `${p.left}%`, top: '-10px',
          width: `${p.size}px`, height: `${p.size * 0.6}px`,
          backgroundColor: p.color, borderRadius: '1px',
          transform: `rotate(${p.rotation}deg)`,
          animation: `confettiFall ${p.duration}s ease-out ${p.delay}s forwards`,
        }} />
      ))}
      <style>{`@keyframes confettiFall { 0%{transform:translateY(0) rotate(0deg);opacity:1} 100%{transform:translateY(100vh) rotate(720deg);opacity:0} }`}</style>
    </div>
  );
}
